import React, { useMemo } from 'react';
import { Box, GlobalStyles } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { BRAND } from './brandTokens';
import { buildBrandCssVariables, buildBrandPalette } from './buildBrandPalette';
import createAppTheme from './createAppTheme';

/**
 * `app` sets the Salovare defaults on :root; `store` overrides them with a
 * store's palette (html:root wins over :root and is removed on unmount);
 * `local` scopes the palette to its children, e.g. for a settings preview.
 */
const SCOPE_SELECTORS = {
  app: ':root',
  store: 'html:root',
};

const BrandThemeProvider = ({ colors, scope = 'app', children }) => {
  const { primary, accent, text, background } = colors || {};
  const palette = useMemo(
    () => buildBrandPalette({ primary, accent, text, background }),
    [primary, accent, text, background],
  );
  const theme = useMemo(() => createAppTheme(palette), [palette]);
  const variables = useMemo(() => buildBrandCssVariables(palette), [palette]);

  if (scope === 'local') {
    return (
      <ThemeProvider theme={theme}>
        <Box style={variables} sx={{ display: 'contents' }}>
          {children}
        </Box>
      </ThemeProvider>
    );
  }

  const styles = { [SCOPE_SELECTORS[scope] || SCOPE_SELECTORS.app]: variables };
  if (scope === 'app') {
    styles.body = { backgroundColor: BRAND.background, color: BRAND.text };
  }

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles styles={styles} />
      {children}
    </ThemeProvider>
  );
};

export default BrandThemeProvider;
